import React, { useContext, useMemo, useState } from "react";
import { ActionIcon, Badge, Button, Grid, Group, Space, Stack, Text, Title, Tooltip } from "@mantine/core";
import DataTable, { TableColumn } from "react-data-table-component";
import { Configure, HitsPerPage, InstantSearch, InstantSearchProps, Pagination, SearchBox, useHits, useInstantSearch, useSortBy } from "react-instantsearch";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { BaseResult, Campaign, CustomClaims } from "../../types";
import { useFirestore, useFunctions } from "../../helpers/firebaseContext";
import { BsPencil, BsPlusCircle, BsSend } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { notifications } from "@mantine/notifications";
import { MyUserDataContext } from "../../contexts";
import CampaignErrors from "./campaign_errors";
import CampaignDetailsModal from "./campaign_details_modal";
import { exportCampaignsExcel } from "./export_campaigns_excel";
import { useSessionStorage } from "@mantine/hooks";
import { IconEye, IconFileSpreadsheet, IconRefresh } from "@tabler/icons-react";

const TYPESENSE_COLLECTION = import.meta.env.VITE_TYPESENSE_CAMPAIGNS_COLLECTION;

interface InitialState {
  page?: number;
  sortBy?: string;
  hitsPerPage?: number;
}

type TypesenseCampaign = Omit<Campaign, "created_on" | "run_on"> & {
  objectID?: string;
  created_on?: unknown;
  run_on?: unknown;
};

const toCampaignDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const parsedDate = new Date(value > 1_000_000_000_000 ? value : value * 1000);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof value === "string") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof value === "object" && value !== null) {
    if ("toDate" in value && typeof value.toDate === "function") {
      const parsedDate = value.toDate();
      return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
    }

    if ("seconds" in value && typeof value.seconds === "number") {
      const parsedDate = new Date(value.seconds * 1000);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
  }

  return null;
};

const formatCampaignDate = (value: unknown) => toCampaignDate(value)?.toLocaleString("en-gb") ?? "-";

const CampaignsTable = ({
  canManageCampaigns,
  isKfhUser,
  loadingId,
  runCampaign,
}: {
  canManageCampaigns: boolean;
  isKfhUser: boolean;
  loadingId: string;
  runCampaign: (id: string) => Promise<void>;
}) => {
  const navigate = useNavigate();
  const { items } = useHits<TypesenseCampaign>();
  const [detailsCampaignId, setDetailsCampaignId] = useState<string | null>(null);
  const { refine } = useSortBy({
    items: [
      { label: "Created (desc)", value: `${TYPESENSE_COLLECTION}/sort/created_on:desc` },
      { label: "Created (asc)", value: `${TYPESENSE_COLLECTION}/sort/created_on:asc` },
    ],
  });

  function buildCounters(campaign: TypesenseCampaign) {
    const rows: JSX.Element[] = [];
    let totalSum = 0;
    let sentSum = 0;
    let FailedSum = 0;

    if (campaign.email) {
      totalSum += campaign.email.message_total;
      sentSum += campaign.email.message_sent;
      FailedSum += campaign.email.message_failed;
      rows.push(
        <tr key="email">
          <td>Email</td>
          <td>{campaign.email.message_total}</td>
          <td>{campaign.email.message_sent}</td>
          <td>{campaign.email.message_failed}</td>
        </tr>
      );
    }
    if (campaign.whatsapp) {
      totalSum += campaign.whatsapp.message_total;
      sentSum += campaign.whatsapp.message_sent;
      FailedSum += campaign.whatsapp.message_failed;
      rows.push(
        <tr key="whatsapp">
          <td>Whatsapp</td>
          <td>{campaign.whatsapp.message_total}</td>
          <td>{campaign.whatsapp.message_sent}</td>
          <td>{campaign.whatsapp.message_failed}</td>
        </tr>
      );
    }
    if (campaign.sms) {
      totalSum += campaign.sms.message_total;
      sentSum += campaign.sms.message_sent;
      FailedSum += campaign.sms.message_failed;
      rows.push(
        <tr key="sms">
          <td>SMS</td>
          <td>{campaign.sms.message_total}</td>
          <td>{campaign.sms.message_sent}</td>
          <td>{campaign.sms.message_failed}</td>
        </tr>
      );
    }
    if (campaign.custom_email) {
      totalSum += campaign.custom_email.message_total;
      sentSum += campaign.custom_email.message_sent;
      FailedSum += campaign.custom_email.message_failed;
      rows.push(
        <tr key="custom_email">
          <td>Custom Email</td>
          <td>{campaign.custom_email.message_total}</td>
          <td>{campaign.custom_email.message_sent}</td>
          <td>{campaign.custom_email.message_failed}</td>
        </tr>
      );
    }
    if (campaign.notification) {
      totalSum += campaign.notification.message_total;
      sentSum += campaign.notification.message_sent;
      FailedSum += campaign.notification.message_failed;
      rows.push(
        <tr key="notification">
          <td>Notification</td>
          <td>{campaign.notification.message_total}</td>
          <td>{campaign.notification.message_sent}</td>
          <td>{campaign.notification.message_failed}</td>
        </tr>
      );
    }

    rows.push(
      <tr key="total">
        <td>Total</td>
        <td>{totalSum}</td>
        <td>{sentSum}</td>
        <td>{FailedSum}</td>
      </tr>
    );

    const table = (
      <table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Total</th>
            <th>Sent</th>
            <th>Failed</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    );

    return (
      <Tooltip label={table}>
        <Grid>
          <Grid.Col>
            <CampaignErrors campaign={campaign as unknown as Campaign} FailedSum={FailedSum}>
              <span>{sentSum + "/" + totalSum}</span>
            </CampaignErrors>
          </Grid.Col>
        </Grid>
      </Tooltip>
    );
  }

  const columns = useMemo<TableColumn<TypesenseCampaign>[]>(() => {
    const tableColumns: TableColumn<TypesenseCampaign>[] = [
      {
        name: "Title",
        selector: (row) => row.title ?? "",
        sortable: true,
        id: "title",
        grow: 2,
      },
      {
        name: "Types",
        wrap: true,
        cell: (row) => (
          <>
            {(row.type?.includes("email") || row.email) && <Badge m={5} size="xs" variant="filled" color="gray">Email</Badge>}
            {(row.type?.includes("customEmail") || row.custom_email) && <Badge m={5} size="xs" variant="filled" color="blue">Custom Email</Badge>}
            {(row.type?.includes("sms") || row.sms) && <Badge m={5} size="xs" variant="filled" color="pink">SMS</Badge>}
            {(row.type?.includes("whatsapp") || row.whatsapp) && <Badge m={5} size="xs" variant="filled" color="green">Whatsapp</Badge>}
            {(row.type?.includes("notification") || row.notification) && <Badge m={5} size="xs" variant="filled" color="yellow">Notification</Badge>}
          </>
        ),
      },
      {
        name: "Status",
        id: "status",
        selector: (row) => row.status ?? "",
        sortable: true,
        cell: (row) => {
          if (row.status === "waiting_for_approve") {
            return <Badge size="xs" variant="filled" color="gray">Waiting for approval</Badge>;
          }

          if (row.status === "done") {
            return <Badge size="xs" variant="filled" color="green">Done</Badge>;
          }

          return null;
        },
      },
      {
        name: "Created Date",
        id: "created_on",
        selector: (row) => toCampaignDate(row.created_on)?.getTime() ?? 0,
        cell: (row) => (
          <Tooltip label={"Created By " + (row.created_by ?? "")}>
            <span>{formatCampaignDate(row.created_on)}</span>
          </Tooltip>
        ),
        sortable: true,
      },
      {
        name: "Created By",
        id: "created_by",
        selector: (row) => row?.created_by,
        cell: (row) => (

          <span>{(row.created_by)}</span>

        ),
        sortable: true,
      },
      {
        name: "Details",
        width: "80px",
        ignoreRowClick: true,
        cell: (row) => (
          <Tooltip label="View details">
            <ActionIcon variant="subtle" color="blue" onClick={() => setDetailsCampaignId(row.id)}>
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ];

    if (!isKfhUser) {
      tableColumns.push(
        {
          name: "Run Date",
          id: "run_on",
          selector: (row) => toCampaignDate(row.run_on)?.getTime() ?? 0,
          cell: (row) => (
            <Tooltip label={"Run By " + (row.run_by ?? "")}>
              <span>{formatCampaignDate(row.run_on)}</span>
            </Tooltip>
          ),
          sortable: true,
        },
        {
          name: "Sent Count",
          ignoreRowClick: true,
          cell: (row) => buildCounters(row),
        }
      );
    }

    if (canManageCampaigns) {
      tableColumns.push({
        name: "Actions",
        width: "100px",
        ignoreRowClick: true,
        cell: (row) => (
          <Group my={10} gap="xs" wrap="nowrap">
            <Tooltip label={row.navCampaignSource && row.status === "done" ? "Resume unfinished deliveries" : "Send"}>
              <ActionIcon
                onClick={() => runCampaign(row.id)}
                variant="outline"
                color="blue"
                loading={loadingId === row.id}
                disabled={(row.status !== "waiting_for_approve" && !(row.navCampaignSource && row.status === "done")) || loadingId !== ""}
              >
                <BsSend />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Edit">
              <ActionIcon
                onClick={() => navigate(`/campaigns/edit/${row.id}`)}
                variant="outline"
                color="blue"
                disabled={!!row.navCampaignSource || row.status !== "waiting_for_approve" || loadingId !== ""}
              >
                <BsPencil />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
      });
    }

    return tableColumns;
  }, [canManageCampaigns, isKfhUser, loadingId, navigate, runCampaign]);

  return (
    <>
      <DataTable
        columns={columns}
        data={items}
        sortServer
        highlightOnHover
        pointerOnHover
        onRowClicked={(row) => setDetailsCampaignId(row.id)}
        onSort={(column, sortDirection) => {
          if (column.id) {
            refine(`${TYPESENSE_COLLECTION}/sort/${column.id}:${sortDirection}`);
          }
        }}
        noDataComponent={(
          <Text size="sm" c="dimmed" py="lg">
            No campaigns found.
          </Text>
        )}
      />
      <CampaignDetailsModal
        campaignId={detailsCampaignId}
        onClose={() => setDetailsCampaignId(null)}
      />
    </>
  );
};

const RefreshSearchButton = () => {
  const { refresh } = useInstantSearch();

  return (
    <Button leftSection={<IconRefresh size={16} />} onClick={() => refresh()}>
      Refresh
    </Button>
  );
};
const Campaigns: React.FC = () => {
  const functions = useFunctions();
  const db = useFirestore();
  const navigate = useNavigate();
  const userContext = useContext(MyUserDataContext);
  const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
  const isKfhUser = Boolean(userClaims.kfh_rm || userClaims.kfh_oper);
  const canManageCampaigns = !isKfhUser;
  const [loadingId, setLoadingId] = useState("");
  const [exporting, setExporting] = useState(false);
  const indexName = TYPESENSE_COLLECTION;
  const campaignsSearchKey = userContext.campaignsSearchKey || userContext.customSearchKey;

  const typesenseInstantsearchAdapter = useMemo(() => new TypesenseInstantSearchAdapter({
    server: {
      apiKey: campaignsSearchKey!,
      nodes: [
        {
          host: import.meta.env.VITE_TYPESENSE_SEARCH_HOST!,
          port: 443,
          protocol: "https",
        },
      ],
    },
    additionalSearchParameters: {
      query_by: "title,created_by,status,type,rm.name_en,rm.name_ar,rm.email,rm.rm_id,rm.segment",
    },
  }), [campaignsSearchKey]);

  const typesenseSearchClient = typesenseInstantsearchAdapter.searchClient;
  const searchStateScope = `${userContext.user?.uid ?? "anon"}_${(campaignsSearchKey ?? "").slice(0, 12)}`;
  const [initialUIStateForIndex, setInitialUIStateForIndex] = useSessionStorage<InitialState>({
    key: `initialUIStateFor_${indexName}_${searchStateScope}`,
    getInitialValueInEffect: false,
  });

  const initialIndexUiState = useMemo(() => ({
    ...(initialUIStateForIndex ?? {}),
    sortBy: `${indexName}/sort/created_on:desc`,
  }), [indexName, initialUIStateForIndex]);

  const onStateChange: InstantSearchProps["onStateChange"] = ({ uiState, setUiState }) => {
    setInitialUIStateForIndex({
      ...(uiState[indexName] ?? {}),
      sortBy: `${indexName}/sort/created_on:desc`,
    });
    setUiState(uiState);
  };

  async function runCampaign(id: string) {
    try {
      setLoadingId(id);
      const runCampaign = httpsCallable<string, BaseResult>(functions, "runCampaign");
      await runCampaign(id);
      setLoadingId("");
    } catch (error: any) {
      notifications.show({
        position: "top-right",
        color: "red",
        title: "Failed to run campaign",
        message: error?.message,
      });
      setLoadingId("");
    }
  }

  async function exportReport() {
    try {
      setExporting(true);
      const count = await exportCampaignsExcel(db);
      notifications.show({
        position: "top-right",
        color: "green",
        title: "Report downloaded",
        message: `Exported ${count} campaigns to Excel`,
      });
    } catch (error: any) {
      notifications.show({
        position: "top-right",
        color: "red",
        title: "Failed to export report",
        message: error?.message,
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <Group justify="space-between">
        <Title>Campaigns</Title>
        {canManageCampaigns && (
          <Group>
            <Button
              variant="outline"
              leftSection={<IconFileSpreadsheet size={16} />}
              loading={exporting}
              onClick={exportReport}
            >
              Export Excel
            </Button>
            <Button
              leftSection={<BsPlusCircle />}
              onClick={() => {
                navigate("/campaigns/create");
              }}
            >
              Create Campaign
            </Button>
          </Group>
        )}
      </Group>

      <Space h="xl" />

      <Stack gap="md">
        {/* <Group gap="xs" align="center">
          <Badge color="blue" variant="light">
            Typesense mode
          </Badge>
        </Group> */}

        <InstantSearch
          searchClient={typesenseSearchClient}
          indexName={indexName}
          initialUiState={{ [indexName]: initialIndexUiState }}
          onStateChange={onStateChange}
        >
          <Configure exactOnSingleWordQuery="attribute" />
          <Group justify="end" mb="md">
            <RefreshSearchButton />
            <SearchBox
              autoFocus
              placeholder="Search campaigns..."
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "50%",
              }}
            />
          </Group>
          <CampaignsTable
            canManageCampaigns={canManageCampaigns}
            isKfhUser={isKfhUser}
            loadingId={loadingId}
            runCampaign={runCampaign}
          />
          <Group justify="end" mt="md">
            <HitsPerPage
              items={[
                { label: "250", value: 250 },
                { label: "100", value: 100 },
                { label: "50", value: 50, default: true },
              ]}
            />
          </Group>
          <Space h="xl" />
          <Pagination
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          />
        </InstantSearch>
      </Stack>
    </>
  );
};

export default Campaigns;
